import { User as Auth0User, useAuth0 } from '@auth0/auth0-react';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { useGetMeQuery } from '@/api/api-hooks';
import { type ContextMode, contextModeOf } from '@/api/types/contextMode';
import { UserRoleItem, userRoleItems } from '@/components/User/userRoleItems';

interface User {
  userName: string;
  userId: string;
  email: string;
  entitlements: string[];
  featureFlags: string[];
  isMeLoading: boolean;
  isDriverAiUser: boolean | undefined;
  isDriverSupport: boolean;
  user?: Auth0User;
  organizationId: string | null;
  organizationName: string | null;
  organizationDisplayName: string | null;
  organizationLogoUrl: string | null;
  is404: boolean;
  isAuthenticated: boolean;
  /**
   * True when the organization was created through public signup, read from
   * `/me` (organization metadata). Gates self-serve onboarding UI and feeds
   * analytics; the Auth0 ID token carries no equivalent claim.
   */
  isSelfServiceOrg: boolean;
  /**
   * True when public signup named the organization after the founder's email
   * because no workspace name was given; the org switcher shows "My Driver".
   */
  isOrgNamedAfterSignupEmail: boolean;
  /**
   * True when the current user is the one who signed the organization up
   * through public signup, read from `/me`. Invited teammates share
   * `isSelfServiceOrg` but not this; founder-only onboarding keys on it.
   */
  isSelfServiceFounder: boolean;
  /**
   * How the organization's codebases are served, read from `/me`. `LEGACY`
   * until the profile arrives, and on a backend from before the mode: today's
   * behaviour is the fail-open answer. `useContextMode` is the usual reader.
   */
  contextMode: ContextMode;
  orgRole?: UserRoleItem;
  isSuperAdmin: boolean;
  isSourceAdmin: boolean;
  isTeamAdmin: boolean;
  /**
   * Returns the organizationName from the URL instead of Auth0 and defaults to empty if it's not present.
   * This avoids having to wait for Auth0 to load. The 404 gate should take over in the case where
   * routeOrganizationName doesn't match organizationName.
   */
  routeOrganizationName: string;
}

export const isDriverEmail = (email: string): boolean => {
  const driverAiDomains = ['@driverai.com', '@driver.ai'];
  return driverAiDomains.some((domain) => email.includes(domain));
};

export const useUser = (): User => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const { data: meData, isLoading: isMeLoading } = useGetMeQuery({
    enabled: isAuthenticated,
    refetchInterval: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60 * 5, // data stays fresh for 5 minutes
  });
  const actualIsMeLoading = !isAuthenticated || isMeLoading;
  const isDriverAiUser = isDriverEmail(user?.email ?? '');
  const { organizationName: routeOrganizationName } = useParams({ strict: false });

  const userName: string = user?.name || '';
  const email: string = user?.email || '';
  const userId: string = user?.sub || '';
  const organizationId = user?.org_id ?? null;
  const organizationName = user?.org_name ?? null;
  const organizationDisplayName = user?.org_display_name ?? null;
  const organizationLogoUrl = user?.org_logo_url ?? null;

  const is404 = !!organizationName && !!routeOrganizationName && organizationName !== routeOrganizationName;

  const orgRole = useMemo(() => userRoleItems.find((item) => item.value === meData?.org_role), [meData?.org_role]);

  return {
    userName,
    userId,
    email,
    entitlements: meData?.entitlements ?? [],
    featureFlags: meData?.feature_flags ?? [],
    isMeLoading: isLoading || actualIsMeLoading,
    isDriverAiUser,
    isDriverSupport: meData?.is_driver_support ?? false,
    user,
    organizationId,
    organizationName,
    organizationDisplayName,
    organizationLogoUrl,
    is404,
    isAuthenticated,
    isSelfServiceOrg: meData?.self_service ?? false,
    isOrgNamedAfterSignupEmail: meData?.org_named_after_signup_email ?? false,
    isSelfServiceFounder: meData?.self_service_founder ?? false,
    contextMode: contextModeOf(meData?.context_mode),
    orgRole,
    isSuperAdmin: meData?.org_role === 'org_super_admin',
    isSourceAdmin: meData?.source_admin ?? false,
    isTeamAdmin: meData?.team_admin ?? false,
    routeOrganizationName: routeOrganizationName ?? '',
  };
};
